import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Customer, CustomerStatus } from 'types/customers'
import { SourceWallet } from 'types/move/move'
import { TransferWalletsReponse, WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { formatString, roundNumberWithCommas } from 'utils/utils'

interface UseFormOptionsProps {
  customerList: Customer[]
  transferWallets: TransferWalletsReponse[]
  sourceWallet: SourceWallet
}

export const useFormOptions = ({
  customerList,
  sourceWallet,
  transferWallets,
}: UseFormOptionsProps) => {
  const { t } = useTranslation()

  const customerOptions = useMemo(() => {
    return customerList
      ?.filter?.(customer => customer?.status === CustomerStatus?.VERIFIED)
      ?.map?.(customer => ({
        value: customer?.reference,
        label: customer?.name,
      }))
  }, [customerList])

  const transferWalletOptions = transferWallets?.map(wallet => {
    const code = wallet?.balance?.currency
    const approximateBalance = roundNumberWithCommas(wallet?.balance?.value, 2)
    return {
      icon: code?.toLowerCase(),
      label: wallet?.name,
      value: wallet?.walletId,
      secondLabel: ` ${approximateBalance} ${code}`,
    }
  })

  const selectedSourceWalletOption = useMemo(() => {
    if (sourceWallet) {
      return [
        {
          label: sourceWallet?.walletName,
          value: sourceWallet?.walletId,
          icon: sourceWallet?.currency?.toLowerCase(),
          secondLabel: formatString(
            t('balanceWithCurrency'),
            roundNumberWithCommas(sourceWallet?.balance, 2),
            sourceWallet?.currency
          ),
        },
      ]
    }

    return []
  }, [sourceWallet])

  const walletTransferTypeOptions = [
    {
      label: t('customers.toAnotherWallet'),
      value: WalletTransferType.MAIN,
    },
    {
      label: t('customers.toACustomerWallet'),
      value: WalletTransferType.CUSTOMER,
    },
  ]
  return {
    customerOptions,
    transferWalletOptions,
    selectedSourceWalletOption,
    walletTransferTypeOptions,
  }
}
